import React from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface AdultConsentDialogProps {
  open: boolean;
  checked: boolean;
  loading: boolean;
  errorText?: string;
  onCheckedChange: (checked: boolean) => void;
  onOpenSecuritySettings: () => void;
  onSave: () => void;
}

const AdultConsentDialog: React.FC<AdultConsentDialogProps> = ({
  open,
  checked,
  loading,
  errorText,
  onCheckedChange,
  onOpenSecuritySettings,
  onSave,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
      }}
    >
      <DialogTitle>{t('adult_consent_title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('adult_consent_description')}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={(event) => onCheckedChange(event.target.checked)}
              disabled={loading}
            />
          }
          label={t('adult_consent_checkbox_label')}
        />
        <FormHelperText
          error={Boolean(errorText)}
          sx={{ mt: 0, color: errorText ? 'error.main' : 'text.secondary' }}
        >
          {errorText ?? t('adult_consent_required')}
        </FormHelperText>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t('adult_consent_decline_helper')}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="text"
          onClick={onOpenSecuritySettings}
          disabled={loading}
        >
          {t('security')}
        </Button>
        <Button variant="contained" onClick={onSave} disabled={loading}>
          {t('adult_consent_confirm_label')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdultConsentDialog;
